import React, { useRef, useState } from "react";
import { SendPresentButton, BuyShardsButton, AuthButton, ProfileButton, HomeButton, LocalizationsButton } from "../functional/buttons";
import { LanguageIcon, LogoutIcon, HeaderLinkMenuIcon, NavMenuIcon, CloseIcon } from "../ui/icons";
import { useLocation } from "react-router-dom";
import { HeaderLink } from "./models/header";
import { OnChangeLocation, OnClickOutside, SubscribeGlobalState } from "../../../common/hooks/global-state";
import { AuthorizedUser } from "../../../api/repositories/backend/models/auth";
import { localize } from "../../../common/services/localization";

export const HeaderMenuLinks = [
    {
        localName: "header_games",
        childs: [
            {
                localName: "header_games_search",
                href: "/matches/search"
            }
        ]
    },
    {
        localName: "header_players",
        childs: [
            {
                localName: "header_leaderboard",
                href: "/leaderboard"
            },
            {
                localName: "header_players_search",
                href: "/players/search"
            }
        ]
    },
    {
        localName: "header_wiki",
        childs: [
            {
                localName: "header_meta",
                href: "/heroes/meta"
            },
            {
                localName: "header_heroes",
                href: "/heroes"
            },
            {
                localName: "header_updates",
                href: "/updates"
            }
        ]
    },
] as HeaderLink[];

export const Header: React.FC<{}> = () => {
    const authorizedUser = SubscribeGlobalState('authorizedUser') as AuthorizedUser | undefined;
    const language = SubscribeGlobalState('language') as Language | undefined

    const mobileNavigationsRef = useRef<HTMLDivElement>(null)
    const [showMobileNavigations, setShowMobileNavigations] = useState(false);

    OnClickOutside(mobileNavigationsRef, () => {
        setShowMobileNavigations(false);
    })

    OnChangeLocation(() => {
        setShowMobileNavigations(false);
    })

    return (
        <div className="w-full relative">
            <div className="w-full flex justify-center xl:mt-[20px]">
                <HeaderSM onClickMobileMenu={() => setShowMobileNavigations(!showMobileNavigations)} authorizedUser={authorizedUser} language={language} />
                <HeaderMD onClickMobileMenu={() => setShowMobileNavigations(!showMobileNavigations)} authorizedUser={authorizedUser} language={language} />
                <HeaderLG onClickMobileMenu={() => setShowMobileNavigations(!showMobileNavigations)} authorizedUser={authorizedUser} language={language} />
                <HeaderXL authorizedUser={authorizedUser} language={language} />
            </div>
            {showMobileNavigations
                ? <div className="xl:hidden z-[20] fixed inset-0 flex items-center justify-center bg-[rgba(0,0,0,0.8)]" >
                    <div ref={mobileNavigationsRef}>
                        <NavMobileMenu authorizedUser={authorizedUser} language={language} onClickClose={() => setShowMobileNavigations(false)} />
                    </div>
                </div>
                : <></>
            }
            {/* {
                <div className="z-[20] fixed inset-0 flex items-center justify-center bg-[rgba(0,0,0,0.8)]" >
                    <div ref={mobileNavigationsRef}>
                        <NavMobileMenu authorizedUser={authorizedUser} language={language} onClickClose={() => setShowMobileNavigations(false)} />
                    </div>
                </div>
            } */}
        </div>
    );
};

export const NavMobileMenuButton: React.FC<{
    className?: string
    isOpen?: boolean
    onClick: () => void
}> = ({ className, onClick, isOpen }) => {

    return <button
        onClick={onClick}
    >
        <NavMenuIcon isActive={isOpen ?? false} />
    </button>
}

export const NavMobileMenu: React.FC<{
    className?: string
    onClickClose: () => void
    authorizedUser?: AuthorizedUser
    language?: Language
}> = ({ className, onClickClose, language, authorizedUser }) => {
    return <div className="bg-dark shadow-dark-l mt-[0px] px-[20px] py-[80px] top-0 right-0 w-[80%] sm:w-1/2 z-[100] absolute h-screen ">
        <div className="flex flex-col h-full gap-y-[40px]">
            <div className="flex flex-col gap-[12px]">
                {authorizedUser ? (
                    <div className="flex flex-row justify-between items-center">
                        <HeaderShardBlock authUser={authorizedUser} />
                        <ProfileButton playerId={authorizedUser.playerId} />
                    </div>
                ) : (
                    <AuthButton />
                )}
                <div className="flex flex-col w-full gap-[12px]">
                    {language === "ru" && <SendPresentButton isAuthorized={authorizedUser?.authorized} textClassName="text-[14px]" />}
                    <BuyShardsButton isAuthorized={authorizedUser?.authorized} textClassName="text-[14px]" />
                </div>
            </div>

            <div className="flex flex-col gap-[18px]">
                {HeaderMenuLinks.map((m) => (
                    <NavMobileMenuLink key={m.localName} link={m} />
                ))}
            </div>
        </div>
        <button className="absolute top-5 right-5 text-[18px] font-bold brightness-[200%]"
            onClick={onClickClose}
        >
            <CloseIcon />
        </button>
    </div>
}

const HeaderSM: React.FC<{
    authorizedUser?: AuthorizedUser
    language?: Language
    onClickMobileMenu: () => void
}> = ({ authorizedUser, language, onClickMobileMenu }) => {
    return (
        <div className="block md:hidden  w-full relative">
            <div className="max-w-[626px] mx-auto flex items-center justify-between py-2">
                <div className="flex items-center gap-[48px]">
                    <HomeButton />
                </div>
                <div className="flex items-center gap-6">
                    <LocalizationsButton />
                    <NavMobileMenuButton onClick={onClickMobileMenu} />
                </div>
            </div>
        </div>
    );
};

const HeaderMD: React.FC<{
    authorizedUser?: AuthorizedUser
    language?: Language
    onClickMobileMenu: () => void
}> = ({ authorizedUser, language, onClickMobileMenu }) => {
    return (
        <div className="hidden md:block lg:hidden xl:hidden w-full relative">
            <div className="max-w-[940px] mx-auto flex items-center justify-between py-2">
                <div className="flex items-center gap-[48px]">
                    <HomeButton />
                </div>
                <div className="flex items-center gap-6">
                    <LocalizationsButton />
                    {language === "ru" && <SendPresentButton isAuthorized={authorizedUser?.authorized} textClassName="text-[14px]" />}
                    <BuyShardsButton isAuthorized={authorizedUser?.authorized} textClassName="text-[14px]" />
                    {authorizedUser ? (
                        <>
                            <HeaderShardBlock authUser={authorizedUser} />
                        </>
                    ) : (
                        <AuthButton />
                    )}
                    <NavMobileMenuButton onClick={onClickMobileMenu} />
                </div>
            </div>
        </div>
    );
};

const HeaderLG: React.FC<{
    authorizedUser?: AuthorizedUser;
    language?: Language;
    onClickMobileMenu: () => void
}> = ({ authorizedUser, language, onClickMobileMenu }) => {
    return (
        <div className="hidden lg:block xl:hidden w-full relative">
            <div className="max-w-[960px] mx-auto flex items-center justify-between py-2">
                <div className="flex items-center gap-[48px]">
                    <HomeButton />
                </div>
                <div className="flex items-center gap-6">
                    <LocalizationsButton />
                    {language === "ru" && <SendPresentButton isAuthorized={authorizedUser?.authorized} />}
                    <BuyShardsButton isAuthorized={authorizedUser?.authorized} />
                    {authorizedUser ? (
                        <>

                            <HeaderShardBlock authUser={authorizedUser} />
                            <ProfileButton playerId={authorizedUser.playerId} />
                        </>
                    ) : (
                        <AuthButton />
                    )}
                    <NavMobileMenuButton onClick={onClickMobileMenu} />
                </div>
            </div>
        </div>
    );
};

const HeaderXL: React.FC<{
    authorizedUser?: AuthorizedUser;
    language?: Language;
}> = ({ authorizedUser, language }) => {
    return (
        <div className="hidden xl:block w-full px-6">
            <div className="max-w-[1560px] mx-auto flex items-center justify-between py-2">
                <div className="flex items-center gap-[48px]">
                    <HomeButton />
                    <div className="flex items-center gap-[24px]">
                        {HeaderMenuLinks.map((m) => (
                            <HeaderLinkMenu key={m.localName} link={m} />
                        ))}
                    </div>
                </div>
                <div className="flex items-center gap-6">
                    <LocalizationsButton />
                    {language === 'ru' && <SendPresentButton isAuthorized={authorizedUser?.authorized} />}
                    <BuyShardsButton isAuthorized={authorizedUser?.authorized} />
                    {authorizedUser ? (
                        <>
                            <HeaderShardBlock authUser={authorizedUser} />
                            <ProfileButton playerId={authorizedUser.playerId} />
                        </>
                    ) : (
                        <AuthButton />
                    )}
                </div>
            </div>
        </div>
    );
};


const HeaderShardBlock: React.FC<{
    className?: string
    authUser?: AuthorizedUser
}> = ({ className, authUser }) => {
    return <div className={`flex flex-row ${className} items-center`}>
        <img className="mr-2" src="/images/shards.png" />
        <div className=" justify-start text-white text-[20px] lg:text-[26px] font-bold font-['Reaver'] uppercase 
        leading-7 tracking-tight [text-shadow:_0px_1px_4px_rgb(0_0_0_/_0.57)]">
            {authUser?.shardsAmount}
        </div>
    </div>
}

const NavMobileMenuLink: React.FC<{
    className?: string;
    link: HeaderLink;
}> = ({ link, className }) => {
    const location = useLocation();
    const pathname = location.pathname;

    const isCurrent =
        link.href === pathname ||
        (link.childs && link.childs.some((c) => c.href === pathname));

    return <div>
        <div className={`uppercase text-[18px] font-semibold w-fit ${isCurrent && 'border-orange-b'}`}>
            {localize(link.localName)}
        </div>
        <div className="flex flex-col gap-[8px] px-[12px] py-[8px]">
            {link.childs
                ? link.childs.map((c, i) => <a
                    className={``}
                    href={c.href}
                    key={i}>
                    {localize(c.localName)}
                </a>)
                : null
            }
        </div>
    </div>
}

const HeaderLinkMenu: React.FC<{
    className?: string;
    link: HeaderLink;
}> = ({ link, className }) => {
    const [isOpen, setIsOpen] = useState(false);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const location = useLocation();
    const pathname = location.pathname;

    const isCurrent =
        link.href === pathname ||
        (link.childs && link.childs.some((c) => c.href === pathname));

    const handleMouseEnter = () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        setIsOpen(true);
    };

    const handleMouseLeave = () => {
        timeoutRef.current = setTimeout(() => {
            setIsOpen(false);
        }, 150);
    };

    return (
        <div className={`${className} h-full mr-[32px] inline-flex items-center`}>
            {link.childs ? (
                <div
                    className="dropdown group relative inline-flex items-center h-full"
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                >
                    <div
                        className={`header-link uppercase inline-flex items-center ${isOpen || isCurrent ? 'open' : ''
                            }`}
                    >
                        <span>{localize(link.localName)}</span>
                        <HeaderLinkMenuIcon
                            className={`ml-1 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''
                                }`}
                        />
                    </div>
                    <ul className={`dropdown-menu ${isOpen ? 'open' : ''}`}>
                        {link.childs.map((m) => (
                            <li key={m.href}>
                                <a
                                    href={m.href}
                                    aria-current={m.href === pathname ? 'page' : undefined}
                                >
                                    {localize(m.localName)}
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>
            ) : (
                <a
                    key={link.href}
                    aria-current={link.href === pathname ? 'page' : undefined}
                    className={`header-link uppercase ${isCurrent ? 'open' : ''}`}
                    href={link.href}
                >
                    {localize(link.localName)}
                </a>
            )}
        </div>
    );
};